import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, ArrowLeft, LogOut, Upload, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gromza_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);
  const [builds, setBuilds] = useState({
    android: { version: "", file: null, progress: null, busy: false },
    ios: { version: "", file: null, progress: null, busy: false },
  });
  const [buildsList, setBuildsList] = useState(null);

  const authed = (t = token) => ({ headers: { Authorization: `Bearer ${t}` } });

  const loadDashboard = async (t) => {
    try {
      const [leadsRes, buildsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, authed(t)),
        axios.get(`${API}/admin/builds`, authed(t)),
      ]);
      setLeads(leadsRes.data);
      setBuildsList(buildsRes.data.builds);
      setStats({ leads_count: buildsRes.data.leads_count });
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setLeads(null);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/verify`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      loadDashboard(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
    setStats(null);
  };

  const setBuild = (platform, patch) => setBuilds((b) => ({ ...b, [platform]: { ...b[platform], ...patch } }));

  const CHUNK_SIZE = 2 * 1024 * 1024;

  const uploadChunkWithRetry = async (uploadId, chunk, offset, headers, attempts = 3) => {
    for (let a = 0; a < attempts; a++) {
      try {
        await axios.post(`${API}/admin/upload/chunk/${uploadId}?offset=${offset}`, chunk, {
          headers,
          timeout: 120000,
        });
        return;
      } catch (err) {
        if (a === attempts - 1) throw err;
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
  };

  const upload = async (platform) => {
    const build = builds[platform];
    if (!build.file) {
      toast.error("Pehle file choose karein");
      return;
    }
    const file = build.file;
    setBuild(platform, { busy: true, progress: 0 });
    try {
      const { data: init } = await axios.post(
        `${API}/admin/upload/init`,
        { platform, version: build.version, filename: file.name, size: file.size },
        authed()
      );
      const uploadId = init.upload_id;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const headers = { ...authed().headers, "Content-Type": "application/octet-stream" };
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        await uploadChunkWithRetry(uploadId, chunk, i * CHUNK_SIZE, headers);
        setBuild(platform, { progress: Math.round(((i + 1) / totalChunks) * 100) });
      }
      const { data } = await axios.post(`${API}/admin/upload/complete/${uploadId}`, {}, authed());
      toast.success(`"${data.filename}" (${data.size_mb} MB) upload ho gaya aur ab LIVE hai — users ko turant milega`);
      setBuild(platform, { busy: false, progress: null, file: null, version: "" });
      loadDashboard();
    } catch (err) {
      setBuild(platform, { busy: false, progress: null });
      toast.error(err.response?.data?.detail || "Upload failed — dobara try karein");
    }
  };

  const setLive = async (buildId, filename) => {
    try {
      await axios.post(`${API}/admin/set-live`, { build_id: buildId }, authed());
      toast.success(`"${filename}" ab LIVE hai — users yahi download karenge`);
      loadDashboard();
    } catch {
      toast.error("Set live failed");
    }
  };

  const deleteBuild = async (b) => {
    if (!window.confirm(`"${b.filename}" permanently delete kar dein?`)) return;
    try {
      await axios.delete(`${API}/admin/builds/${b.id}`, authed());
      toast.success(`"${b.filename}" delete ho gaya`);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  if (token && leads === null) {
    loadDashboard();
  }

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#0b0f19] text-white relative overflow-hidden">
      <div className="absolute top-[-20%] left-1/3 w-[600px] h-[600px] rounded-full bg-amber-500/[0.06] blur-[130px]" />
      <div className="relative max-w-5xl mx-auto px-6 py-16">
        <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors mb-12">
          <ArrowLeft size={16} /> Back to Gromza
        </Link>

        {!token ? (
          <div className="max-w-md mx-auto mt-16">
            <div data-testid="admin-login-card" className="glass-panel rounded-3xl p-10">
              <div className="w-14 h-14 rounded-2xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center mb-7">
                <Lock size={24} className="text-amber-400" />
              </div>
              <h1 className="font-display text-3xl font-semibold mb-2">
                Admin <span className="gold-text">Portal</span>
              </h1>
              <p className="text-sm text-slate-400 mb-8">Restricted area. Authorised Gromza operators only.</p>
              <form onSubmit={verify} className="space-y-5" data-testid="admin-login-form">
                <div>
                  <label htmlFor="admin-password" className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
                    Password
                  </label>
                  <input
                    id="admin-password"
                    data-testid="admin-password-input"
                    type="password"
                    placeholder="Enter admin password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-5 py-3.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400/50 transition-[border-color] duration-300"
                  />
                </div>
                {error && (
                  <p data-testid="admin-login-error" className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3">
                    {error}
                  </p>
                )}
                <button
                  data-testid="admin-login-button"
                  type="submit"
                  disabled={loading || !password}
                  className="w-full rounded-full bg-amber-400 px-8 py-4 text-sm font-semibold text-[#0b0f19] hover:bg-amber-300 hover:shadow-[0_0_36px_rgba(251,191,36,0.4)] disabled:opacity-50 transition-[background-color,box-shadow] duration-300"
                >
                  {loading ? "Verifying..." : "Unlock Portal"}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <div>
                <h1 className="font-display text-3xl font-semibold">
                  Admin <span className="gold-text">Dashboard</span>
                </h1>
                <p className="text-sm text-slate-400 mt-1">Partner leads, downloads &amp; app builds</p>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="admin-refresh-button"
                  onClick={() => loadDashboard()}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-slate-300 hover:border-amber-400/40 transition-colors"
                >
                  <RefreshCw size={14} /> Refresh
                </button>
                <button
                  data-testid="admin-logout-button"
                  onClick={logout}
                  className="inline-flex items-center gap-2 rounded-full border border-red-400/30 px-5 py-2.5 text-sm text-red-300 hover:bg-red-400/10 transition-colors"
                >
                  <LogOut size={14} /> Logout
                </button>
              </div>
            </div>

            <div className="space-y-8 mb-10" data-testid="admin-build-cards">
              {[
                { key: "android", label: "Android APK", accept: ".apk" },
                { key: "ios", label: "iOS IPA", accept: ".ipa" },
              ].map((pf) => {
                const list = (buildsList || []).filter((b) => b.platform === pf.key);
                const build = builds[pf.key];
                return (
                  <div key={pf.key} className="glass-panel rounded-2xl p-6" data-testid={`admin-build-${pf.key}`}>
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="font-display text-lg font-semibold">{pf.label}</h3>
                      <span className="text-xs text-slate-400" data-testid={`admin-builds-count-${pf.key}`}>
                        {list.length} build{list.length === 1 ? "" : "s"} uploaded
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        data-testid={`admin-version-input-${pf.key}`}
                        type="text"
                        placeholder="Version (e.g. 1.4)"
                        value={build.version}
                        onChange={(e) => setBuild(pf.key, { version: e.target.value })}
                        className="sm:w-40 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400/50 transition-[border-color] duration-300"
                      />
                      <label
                        data-testid={`admin-upload-${pf.key}`}
                        className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-dashed border-amber-400/30 bg-amber-400/[0.05] px-4 py-2.5 text-sm text-amber-300 cursor-pointer hover:bg-amber-400/10 transition-colors"
                      >
                        <Upload size={15} />
                        <span className="truncate max-w-[220px]">{build.file ? build.file.name : "Choose file"}</span>
                        <input
                          type="file"
                          className="hidden"
                          accept={pf.accept}
                          onChange={(e) => setBuild(pf.key, { file: e.target.files[0] || null })}
                        />
                      </label>
                      <button
                        data-testid={`admin-upload-button-${pf.key}`}
                        onClick={() => upload(pf.key)}
                        disabled={build.busy || !build.file}
                        className="rounded-xl bg-amber-400 px-6 py-2.5 text-sm font-semibold text-[#0b0f19] hover:bg-amber-300 disabled:opacity-50 transition-[background-color] duration-300"
                      >
                        {build.busy ? `Uploading ${build.progress ?? 0}%...` : "Upload Build"}
                      </button>
                    </div>
                    {build.progress !== null && (
                      <div className="mt-3" data-testid={`admin-upload-progress-${pf.key}`}>
                        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full bg-amber-400 transition-[width] duration-300" style={{ width: `${build.progress}%` }} />
                        </div>
                        <p className="text-xs text-amber-300 mt-1.5">Uploading... {build.progress}%</p>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500 mt-3 mb-5">
                      Nayi build upload hote hi turant LIVE ho jaati hai — users ko wahi version download hoga.
                    </p>

                    {list.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-white/10">
                              <th className="pb-3 pr-3">Live</th>
                              <th className="pb-3 pr-4">Filename</th>
                              <th className="pb-3 pr-4">Version</th>
                              <th className="pb-3 pr-4">Size</th>
                              <th className="pb-3 pr-4">Uploaded</th>
                              <th className="pb-3 pr-4">Downloads</th>
                              <th className="pb-3 pr-4">Status</th>
                              <th className="pb-3">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {list.map((b) => (
                              <tr key={b.id} className="border-b border-white/[0.05]" data-testid={`build-row-${b.id}`}>
                                <td className="py-3 pr-3">
                                  <input
                                    type="radio"
                                    name={`live-${pf.key}`}
                                    checked={b.status === "live"}
                                    onChange={() => setLive(b.id, b.filename)}
                                    data-testid={`set-live-${b.id}`}
                                    className="accent-amber-400 w-4 h-4 cursor-pointer"
                                  />
                                </td>
                                <td className="py-3 pr-4 text-slate-200 break-all">{b.filename}</td>
                                <td className="py-3 pr-4 text-amber-400 font-semibold">v{b.version}</td>
                                <td className="py-3 pr-4 text-slate-400">{b.size_mb} MB</td>
                                <td className="py-3 pr-4 text-slate-500 whitespace-nowrap">{new Date(b.uploaded_at).toLocaleString("en-IN")}</td>
                                <td className="py-3 pr-4 text-slate-400">{b.downloads}</td>
                                <td className="py-3 pr-4">
                                  {b.status === "live" ? (
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 px-3 py-1 text-[10px] font-bold tracking-widest text-emerald-300">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-glow" /> LIVE
                                    </span>
                                  ) : (
                                    <span className="rounded-full bg-white/[0.05] border border-white/10 px-3 py-1 text-[10px] font-bold tracking-widest text-slate-400">ARCHIVED</span>
                                  )}
                                </td>
                                <td className="py-3">
                                  <button
                                    data-testid={`delete-build-${b.id}`}
                                    onClick={() => deleteBuild(b)}
                                    disabled={b.status === "live"}
                                    title={b.status === "live" ? "Live build delete nahi ho sakti" : "Delete build"}
                                    className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-400/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-500 transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500" data-testid={`admin-builds-empty-${pf.key}`}>Abhi tak koi build upload nahi hui.</p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="glass-panel rounded-2xl p-6" data-testid="admin-leads-table">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-lg font-semibold">Partner Leads</h3>
                <span className="text-xs text-slate-400" data-testid="admin-leads-count">{stats?.leads_count ?? 0} total</span>
              </div>
              {leads && leads.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-white/10">
                        <th className="pb-3 pr-4">Name</th>
                        <th className="pb-3 pr-4">Business</th>
                        <th className="pb-3 pr-4">Phone</th>
                        <th className="pb-3">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.id} className="border-b border-white/[0.05] text-slate-300" data-testid={`admin-lead-row-${l.id}`}>
                          <td className="py-3 pr-4">{l.name}</td>
                          <td className="py-3 pr-4">{l.business_name || "—"}</td>
                          <td className="py-3 pr-4">{l.phone}</td>
                          <td className="py-3 text-slate-500">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-500" data-testid="admin-leads-empty">No leads yet. Share the landing page to start collecting partners.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
